/**
 * Birleşik Masa Ekranı — POS garson akışının çekirdeği.
 *
 * Masaya basınca açılır. Tek kaydırmalı sayfada — yer kazanmak için her
 * blok katlanabilir (collapse):
 *  - DOLU masa  → "Açık Hesap" accordion (kapalıyken başlık + tutar; açınca
 *                 sipariş satırları) + "Hesap Al"
 *  - Her durum  → "Yeni Ürün Ekle" — yanındaki + butonuyla kategori/ürün
 *                 seçimi açılır (varsayılan kapalı)
 *  - Sepet      → en altta "Sepet Detayı" butonu; tıklanınca sepet açılır
 *
 * Ödeme: QR menüdeki gibi tek / parçalı (bölüşmeli). Masada birden fazla
 * açık sipariş varsa garson "hepsi birlikte" ya da "seçili siparişler"
 * arasında seçim yapar.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Group,
  Card,
  SimpleGrid,
  Text,
  Button,
  ActionIcon,
  Divider,
  Badge,
  Loader,
  Center,
  Modal,
  Checkbox,
  Textarea,
  Tooltip,
  NumberInput,
  Select,
  ScrollArea,
  Box,
  Collapse,
  UnstyledButton,
} from "@mantine/core";
import {
  IconPlus,
  IconMinus,
  IconTrash,
  IconChevronDown,
  IconCash,
  IconReceipt2,
  IconToolsKitchen2,
  IconShoppingCart,
  IconCheck,
  IconSparkles,
  IconArrowsExchange,
  IconGift,
  IconX,
  IconCalendarTime,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { EmptyState } from "../../../shared/ui";
import {
  useCategories,
  useProducts,
  useCreateOrder,
  useAddOrderLine,
  useConfirmOrder,
  useTableDetail,
  useReservations,
  useCancelOrder,
  useQrPendingRequests,
  useApproveQrRequest,
  useRejectQrRequest,
  useRequestLineComp,
  useExchangeLine,
  useTenderSession,
  apiClient,
} from "../../../shared/api";
import { PaymentSheet, ShortageDialog, type Shortage } from "../../../shared/ui";
import { RoleGate } from "../../../shared/auth";
import { useQueryClient } from "@tanstack/react-query";
import { TransferLinesModal } from "./TransferLinesModal";

function formatTRY(minor: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
  }).format((minor || 0) / 100);
}

/**
 * Sipariş zamanını "saat" (bugünse) veya "gün saat" (eski/öncekiyse) formatla.
 * POS açık hesap kartında aynı garsonun farklı saatlerde açtığı paralel
 * hesapları ayırt etmek için kullanılır.
 */
function formatOrderTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return time;
  const day = d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
  return `${day} ${time}`;
}

/**
 * Sipariş kitchen_status → POS rozet etiketi + rengi.
 * Garson masa ekranında siparişin mutfak durumunu görür (KDS ile senkron;
 * backend kds._sync_order_kitchen_status günceller). DRAFT/OPEN = onay
 * bekliyor, CONFIRMED/PREPARING/READY/SERVED = mutfak akışı.
 */
const KITCHEN_STATUS_BADGE: Record<string, { label: string; color: string }> = {
  DRAFT: { label: "Onay bekliyor", color: "gray" },
  OPEN: { label: "Onay bekliyor", color: "gray" },
  CONFIRMED: { label: "Onaylandı", color: "blue" },
  PREPARING: { label: "Hazırlanıyor", color: "orange" },
  READY: { label: "Hazır", color: "green" },
  SERVED: { label: "Servis edildi", color: "teal" },
};

type CartItem = {
  id: string;
  name: string;
  price: number;
  qty: number;
  modifier_ids: string[];
  modifier_names: string[];
  // Ad + birim fiyat — sepette ücretli modifier'ı fiyatıyla göstermek için.
  modifiers: { name: string; price_minor: number }[];
  modifiers_total: number;
  notes: string;
  excluded_ingredient_ids?: string[];
  excluded_ingredient_names?: string[];
};


// Masa durum etiketi (Boş/Dolu/...) App header rozetine taşındı; bu ekranda
// yalnız renk eşlemesi kullanılır (masa adı sticker'ının rengi için).
const STATE_COLOR: Record<string, string> = {
  FREE: "green",
  OCCUPIED: "red",
  RESERVED: "yellow",
  CLEANING: "gray",
  OUT_OF_SERVICE: "dark",
};

export function TableScreen({
  tableId,
  onBack,
  onHeaderInfo,
}: {
  tableId: string;
  onBack: () => void;
  /** Masa adı + durum + hesap-istendi bilgisini App header'ına bildirir.
   *  App "POS"un yanına geri ok + ortalanmış durum rozeti basar. Bu ekranın
   *  kendi başlık satırı yok; bilgi yukarı taşındı. */
  onHeaderInfo?: (info: { name: string; state: string; paymentRequested: boolean } | null) => void;
}) {
  const { data: detail, isLoading: detailLoading } = useTableDetail(tableId);

  // Bu masada garson onayı bekleyen QR istekleri (PendingQrRequest). Garson
  // hesap alırken "bir de onaylanmamış sipariş var" diye uyarılır — yoksa
  // hesap kapanınca o istek sessizce iptal olur ve garson fark etmez.
  const { data: qrReqData } = useQrPendingRequests();
  const pendingForTable: any[] = useMemo(() => {
    const all = (qrReqData?.results || qrReqData || []) as any[];
    return all.filter((r) => String(r.table) === String(tableId));
  }, [qrReqData, tableId]);
  const approveReq = useApproveQrRequest();
  const rejectReq = useRejectQrRequest();
  // Hesap Al'a basıldığında bekleyen istek varsa açılan karar modalı.
  const [pendingPrompt, setPendingPrompt] = useState(false);

  const { data: catsData, isLoading: catsLoading } = useCategories();
  const cats: any[] = useMemo(
    () => catsData?.results || catsData || [],
    [catsData],
  );

  // QR menüsüyle aynı 2 katmanlı navigasyon: kök kategori → alt kategori →
  // ürünler. Düz `cats` listesi `parent` alanıyla ağaca ayrılır.
  const rootCats: any[] = useMemo(
    () => cats.filter((c: any) => !c.parent),
    [cats],
  );
  // activeRoot: seçili kök kategori. activeChild: seçili alt kategori.
  const [activeRoot, setActiveRoot] = useState<string | null>(null);
  const [activeChild, setActiveChild] = useState<string | null>(null);
  const childCats: any[] = useMemo(
    () => (activeRoot ? cats.filter((c: any) => c.parent === activeRoot) : []),
    [cats, activeRoot],
  );
  // Ürünler seçili alt kategoriden gelir; alt kategori yoksa (kök doğrudan
  // ürün taşıyorsa) kökün kendisinden.
  const productCat = activeChild || activeRoot;
  const { data: prodsData, isLoading: prodsLoading } = useProducts(
    productCat ? { category: productCat } : undefined,
  );
  // Garson ürünü hızlı bulsun diye alfabetik (Türkçe sıralama: İ/ı/ş/ç/ğ/ö/ü
  // doğru konumlanır — localeCompare("tr")). Kategori içi sıra backend'e bağlı
  // kalmaz, ekranda her zaman A→Z görünür.
  const products: any[] = useMemo(() => {
    const list = (prodsData?.results || prodsData || []) as any[];
    return [...list].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "tr", {
        sensitivity: "base",
        numeric: true,
      }),
    );
  }, [prodsData]);

  // Tüm ürünler (kategori filtresiz) — ürün değişimi modalı için Select datası.
  // Burada da alfabetik (Türkçe) — uzun listede aranan ürün kolay bulunsun.
  const { data: allProdsData } = useProducts();
  const allProducts: any[] = useMemo(() => {
    const list = (allProdsData?.results || allProdsData || []) as any[];
    return [...list].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "tr", {
        sensitivity: "base",
        numeric: true,
      }),
    );
  }, [allProdsData]);

  // Kökün DOĞRUDAN kendisine atanmış ürünleri (alt grubu olsa da) — admin'de
  // köke atanan ürün garsona görünmeli; alt şeride kök adıyla sekme eklenir.
  const rootOwnCount = useMemo(
    () => (activeRoot ? allProducts.filter((p: any) => p.category === activeRoot).length : 0),
    [allProducts, activeRoot],
  );
  const activeRootNode = rootCats.find((c: any) => c.id === activeRoot);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [modifierProduct, setModifierProduct] = useState<any | null>(null);
  const [selectedMods, setSelectedMods] = useState<string[]>([]);
  const [lineNotes, setLineNotes] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  // Stok yetersizliği pop-up'ı — backend add_line 400 + shortages payload
  // döndüğünde açılır; "Sos" gibi is_optional malzemeleri çıkararak retry
  // yapma seçeneği sunar. Zorunlu malzeme eksikse sadece bilgi verir.
  const [shortageDialog, setShortageDialog] = useState<{
    cartItemId: string;
    productName: string;
    shortages: Shortage[];
    /** Bu pop-up tetiklenirken yaratılan ama hiç satır alamayan yetim
     * DRAFT order'ın id'si. Vazgeç / "Hepsini çıkar" anlarında cancel
     * edilir; böylece adminde yüzüsüz TASLAK biriklmez. */
    orphanOrderId: string | null;
  } | null>(null);

  // Katlanabilir bloklar — yer kazanmak için varsayılan kapalı
  const [accountOpen, setAccountOpen] = useState(false); // Açık Hesap accordion
  const [menuOpen, setMenuOpen] = useState(false); // Kategori + ürün seçimi
  const [cartOpen, setCartOpen] = useState(false); // Sepet detayı

  // Sipariş iptal modalı — garson "İptal Et"e basınca açılır. Browser
  // window.confirm yerine Mantine Modal kullanıyoruz; projenin geri kalanı
  // tutarlı kalsın diye native alert box yasak.
  const [cancelTarget, setCancelTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  // İkram (comp) talep modalı — garson bir satır için "müşteri beğenmedi"
  // ikram talebi açar; müdür admin'den onaylar. Talep PENDING; bedel/stok
  // onayda değişir.
  const [compTarget, setCompTarget] = useState<{
    lineId: string;
    label: string;
    maxQty: number;
  } | null>(null);
  const [compQty, setCompQty] = useState<number | string>(1);
  const [compReason, setCompReason] = useState("CUSTOMER_DISSATISFACTION");
  const [compRemake, setCompRemake] = useState(false);

  // Ürün değişimi (exchange) modalı — garson satırı başka ürünle değiştirir.
  // Açılmamışsa stoğa geri (serbest), açılmışsa kayıp (müdür onaylı).
  const [exchangeTarget, setExchangeTarget] = useState<{
    orderId: string;
    lineId: string;
    label: string;
    maxQty: number;
  } | null>(null);
  const [exNewProduct, setExNewProduct] = useState<string | null>(null);
  const [exReturnQty, setExReturnQty] = useState<number | string>(1);
  const [exOpened, setExOpened] = useState(false);
  const [exReason, setExReason] = useState("CHANGED_MIND");

  const productModifiers: any[] = (modifierProduct?.modifiers || []) as any[];
  const createOrder = useCreateOrder();
  const addLine = useAddOrderLine();
  const confirmOrder = useConfirmOrder();
  const qc = useQueryClient();
  const cancelOrder = useCancelOrder();
  const tenderSession = useTenderSession();
  const requestComp = useRequestLineComp();
  const exchangeLine = useExchangeLine();
  const [freeing, setFreeing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  async function markTableFree() {
    if (!tableId) return;
    setFreeing(true);
    try {
      await apiClient.patch(`/api/v1/tables/${tableId}/state/`, {
        state: "FREE",
      });
      notifications.show({ message: "Masa boşaltıldı", color: "green" });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Masa boşaltılamadı",
        color: "red",
      });
    } finally {
      setFreeing(false);
    }
  }

  // İlk kök kategori bir kez seçilir
  useEffect(() => {
    if (rootCats.length > 0 && !activeRoot) {
      setActiveRoot(rootCats[0].id);
    }
  }, [rootCats, activeRoot]);

  // Kök değişince alt kategori seçimini sıfırla — yeni kökün ilk alt
  // kategorisi seçili olur (yoksa null kalır, ürünler kökten gelir).
  useEffect(() => {
    setActiveChild(childCats.length > 0 ? childCats[0].id : null);
  }, [activeRoot, childCats]);

  const table = detail?.table;

  // Bu masaya bağlı onaylı rezervasyon — garson sipariş alırken "bu masa
  // rezervasyondan geldi/gelecek" olduğunu görsün. Masaya tıklayıp açtığı
  // ekranda üst bant olarak gösterilir.
  const { data: reservData } = useReservations({ status: "CONFIRMED", table: tableId });
  const reservation: any = useMemo(() => {
    const list = (reservData?.results || reservData || []) as any[];
    // Yalnızca BUGÜN başlayacak ve henüz geçmemiş rezervasyon gösterilir —
    // ileri tarihli (örn. günler sonrası) rezervasyon canlı POS ekranında
    // "rezervasyonlu masa" bandı basmamalı. Aynı masada birden çok bugünkü
    // rezervasyon varsa en erken başlayan seçilir.
    const now = new Date();
    const todayStr = now.toDateString();
    return list
      .filter((r) => {
        if (String(r.table) !== String(tableId) || !r.starts_at) return false;
        const start = new Date(r.starts_at);
        return start.toDateString() === todayStr && start >= now;
      })
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())[0]
      || null;
  }, [reservData, tableId]);

  const openOrders: any[] = detail?.orders || [];
  const openTotal: number = detail?.total_minor || 0;
  const state = table?.state || "FREE";
  const isOccupied = openOrders.length > 0;
  // QR'dan hesap isteği — varsa üst rozet morlanır + özet kutusu çıkar.
  const paymentRequest = detail?.payment_request || null;
  const paymentRequested = !!paymentRequest;

  // Masa adı + durumu + hesap-istendi bilgisini App header'ına yansıt.
  // Header "POS"un yanına geri ok + ortalanmış durum rozeti basar; bu ekranın
  // ayrı başlık satırı yok. Masadan çıkınca (unmount) null gönderip temizle.
  const tableName = table?.name;
  useEffect(() => {
    onHeaderInfo?.({
      name: tableName || "Masa",
      state,
      paymentRequested,
    });
    return () => onHeaderInfo?.(null);
  }, [onHeaderInfo, tableName, state, paymentRequested]);

  // "Hesap Al" akışı — net kural: masa garson hesap aldıktan sonra
  // BOŞA düşmeli. Üç yol:
  //   1. Tüm açık siparişler 0 TL → ödenecek bir şey yok. Hepsini iptal
  //      et, backend masayı release eder (auto_free_minutes=0 ise FREE).
  //   2. Sadece bazı siparişler 0 TL → onları iptal et, kalanı için
  //      PaymentSheet aç. PaymentSheet kapanınca masa zaten boşalır.
  //   3. Tümü tutarlı → direkt PaymentSheet aç (mevcut davranış).
  async function handleHesapAl() {
    // Masada onay bekleyen QR siparişi varsa önce garsona sor — hesap
    // kapanınca o istek iptal olacak; garson bilinçli karar versin.
    if (pendingForTable.length > 0) {
      setPendingPrompt(true);
      return;
    }
    await proceedHesapAl();
  }

  async function proceedHesapAl() {
    const paidOrders = openOrders.filter((o) => (o.total_minor || 0) > 0);

    // Ödenecek tutar varsa normal akış — PaymentSheet aç.
    if (paidOrders.length > 0) {
      setPayOpen(true);
      return;
    }

    // Ödenecek tutar YOK (tamamı ikram/iade edilmiş veya boş sipariş).
    // İPTAL ETME — servis edilmiş ikram siparişini iptal muhasebeyi bozar
    // (ikram/COGS zaten işlendi). Bunun yerine adisyonu boş tender ile kapat;
    // backend total=0 siparişleri CLOSED yapar, masa boşalır.
    const sessionId = detail?.session?.id;
    if (!sessionId) {
      notifications.show({
        message: "Adisyon bulunamadı — masayı kapatamadım",
        color: "red",
      });
      return;
    }
    setClosing(true);
    try {
      await tenderSession.mutateAsync({ sessionId, tenders: [] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      notifications.show({
        message: "Masa boşaltıldı — ödenecek tutar yoktu (ikram/iade)",
        color: "green",
      });
      onBack();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Hesap kapatılamadı",
        color: "red",
      });
    } finally {
      setClosing(false);
    }
  }

  // Modal aksiyonu: bekleyen istek(ler)i ONAYLA → Order yaratılır, sonra
  // hesap akışına devam. Stok yetersizse uyarı verir, hesap akışını başlatmaz.
  async function approvePendingThenCheckout() {
    try {
      for (const r of pendingForTable) {
        await approveReq.mutateAsync({ requestId: r.id });
      }
      notifications.show({
        message: "Bekleyen sipariş(ler) onaylandı, mutfağa gönderildi",
        color: "green",
      });
      setPendingPrompt(false);
      qc.invalidateQueries({ queryKey: ["table-detail"] });
      // Onaylanan sipariş hesaba eklendi — garson tutarı görsün diye akışı
      // başlatmıyoruz; drawer tazelenince yeni tutarla "Hesap Al"a tekrar basar.
    } catch (e: any) {
      notifications.show({
        message:
          e?.response?.data?.detail ||
          "Onay başarısız — stok yetersiz olabilir, QR Onay ekranından kontrol edin",
        color: "red",
      });
    }
  }

  // Modal aksiyonu: bekleyen istek(ler)i İPTAL ET → sonra hesap akışına devam.
  async function rejectPendingThenCheckout() {
    try {
      for (const r of pendingForTable) {
        await rejectReq.mutateAsync({
          requestId: r.id, reason: "Garson hesap aldı — bekleyen sipariş iptal",
        });
      }
    } catch {
      // İptal hata verse bile hesap akışına devam — masa kapanınca backend
      // kalan PENDING istekleri zaten otomatik iptal eder (güvenlik ağı).
    }
    setPendingPrompt(false);
    await proceedHesapAl();
  }

  // ---- Sepet işlemleri ----
  function addItem(p: any) {
    setModifierProduct(p);
    setSelectedMods([]);
    setLineNotes("");
  }

  function confirmAdd() {
    if (!modifierProduct) return;
    const modsTotal = selectedMods.reduce((s, mid) => {
      const m = productModifiers.find((x) => x.id === mid);
      return s + (m?.price_minor || 0);
    }, 0);
    const modDetails = selectedMods
      .map((mid) => productModifiers.find((x) => x.id === mid))
      .filter(Boolean)
      .map((m: any) => ({ name: m.name, price_minor: m.price_minor || 0 }));
    const modNames = modDetails.map((m) => m.name);
    const itemId = `${modifierProduct.id}|${selectedMods.sort().join(",")}|${lineNotes}`;
    setCart((prev) => {
      const ex = prev.find((c) => c.id === itemId);
      if (ex) {
        return prev.map((c) =>
          c.id === itemId ? { ...c, qty: c.qty + 1 } : c,
        );
      }
      return [
        ...prev,
        {
          id: itemId,
          name: modifierProduct.name,
          price:
            (modifierProduct.unit_price_minor ||
              modifierProduct.price_minor ||
              0) + modsTotal,
          qty: 1,
          modifier_ids: selectedMods,
          modifier_names: modNames,
          modifiers: modDetails,
          modifiers_total: modsTotal,
          notes: lineNotes,
        },
      ];
    });
    setModifierProduct(null);
    // Yeni ürün eklenince açık olan sepet detayını kapat — menü görünür
    // kalsın, kullanıcı seçime devam edebilsin.
    setCartOpen(false);
  }

  function changeQty(id: string, delta: number) {
    setCart((prev) =>
      prev
        .map((c) => (c.id === id ? { ...c, qty: c.qty + delta } : c))
        .filter((c) => c.qty > 0),
    );
  }

  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  async function handleSendToKitchen() {
    if (cart.length === 0) return;
    try {
      // Masada zaten "satır eklenebilir" bir sipariş varsa (örn. QR'dan gelmiş
      // onay bekleyen, ya da daha önce gönderilmiş açık sipariş) — yeni order
      // açmak yerine ona ekle.
      //
      // Eklenebilir durumlar: DRAFT/OPEN (henüz onaylanmamış) +
      // CONFIRMED/PREPARING (mutfak henüz tamamlamamış, ek satır aynı ticket'a
      // sığar).
      //
      // READY/SERVED siparişe yeni satır YAZILMAZ — onun yerine ayrı yeni
      // sipariş açılır. Aksi takdirde "SERVİS EDİLDİ" rozeti yanıltıcı kalır
      // (yeni satırlar henüz mutfağa düşmemiş olur), zaman çizgisi de eski
      // siparişin onay/servis saatini gösterir; aynı oturumun ek isteği için
      // ayrı bir yaşam çizgisi olması net olur. Hesap alırken masadaki tüm
      // siparişler birlikte ödenir, müşteri için tek bir adisyon görünür.
      // CLOSED/CANCELLED'a backend zaten izin vermez.
      const APPENDABLE = new Set([
        "DRAFT", "OPEN",
        "CONFIRMED", "PREPARING",
      ]);
      const existing = openOrders
        .filter((o) => APPENDABLE.has(o.kitchen_status))
        .sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        )[0];

      const order =
        existing ??
        (await createOrder.mutateAsync({
          source: "POS",
          fulfillment_type: "DINE_IN",
          table: tableId,
        }));
      // Bu turda yeni siparişi BIZ yarattıysak ve hiç satır gönderemezsek,
      // arkada yetim DRAFT kalmasın diye cancel etmeliyiz. (Stok yetersizliği
      // pop-up'ında ilk satırda 400 alındığında yaşanır.)
      const createdHere = !existing;

      const sentIds: string[] = [];
      for (const item of cart) {
        const productId = item.id.split("|")[0];
        try {
          await addLine.mutateAsync({
            orderId: order.id,
            product_id: productId,
            quantity: item.qty,
            modifier_ids: item.modifier_ids,
            notes: item.notes,
            ...(item.excluded_ingredient_ids && item.excluded_ingredient_ids.length > 0
              ? { excluded_ingredient_ids: item.excluded_ingredient_ids }
              : {}),
          });
          sentIds.push(item.id);
        } catch (err: any) {
          const data = err?.response?.data;
          const shortages: Shortage[] | undefined = data?.shortages;
          if (shortages && shortages.length > 0) {
            // Bu item için stok yetersiz — sepetten gönderilmiş olanları
            // koru, kalanları (bu dahil) sepette bırak, pop-up aç.
            setCart((prev) => prev.filter((c) => !sentIds.includes(c.id)));
            // Hiç satır eklenememişse ve sipariş bu turda doğduysa yetim
            // DRAFT kalmasın — pop-up'a kaynak ID'yi geçir, vazgeç/exclude
            // sırasında temizleyelim.
            const orphanId =
              createdHere && sentIds.length === 0 ? order.id : null;
            setShortageDialog({
              cartItemId: item.id,
              productName: item.name,
              shortages,
              orphanOrderId: orphanId,
            });
            return;
          }
          throw err;
        }
      }

      // Yeni açılan ya da hâlâ DRAFT/OPEN olan siparişi onayla → mutfağa düşsün.
      // Zaten CONFIRMED+ olan siparişe satır eklendiğinde backend KDS routing'i
      // tetikler (ek ticket olarak düşer), tekrar confirm gerekmez.
      const needsConfirm =
        order.kitchen_status === "DRAFT" || order.kitchen_status === "OPEN";
      if (needsConfirm) {
        await confirmOrder.mutateAsync(order.id);
      }

      notifications.show({
        message: existing
          ? "Satırlar mevcut siparişe eklendi"
          : "Sipariş onaylandı, mutfağa gönderildi",
        color: "green",
      });
      setCart([]);
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Sipariş gönderilemedi",
        color: "red",
      });
    }
  }

  if (detailLoading || catsLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md" pb={cart.length > 0 ? 96 : 24}>
      {/* Başlık satırı App header'ına taşındı: geri ok + masa durumu rozeti
          "POS"un yanında. Burada yalnız masa içeriği kalır. */}

      {/* ===== REZERVASYON — bu masa rezervasyondan geldi/gelecek =====
              Garson sipariş alırken masanın rezerve olduğunu, hangi saatte
              ve hangi misafire ait olduğunu görür. */}
      {reservation && (
        <Card padding="md" radius="md" withBorder bg="yellow.0" style={{ borderColor: "var(--mantine-color-yellow-4)" }}>
          <Group gap={8} wrap="nowrap" align="flex-start">
            <IconCalendarTime size={20} color="var(--mantine-color-yellow-8)" />
            <Box>
              <Text fw={700} c="yellow.8">
                Rezervasyonlu masa ·{" "}
                {new Date(reservation.starts_at).toLocaleString("tr-TR", {
                  day: "2-digit", month: "2-digit",
                  hour: "2-digit", minute: "2-digit",
                })}
              </Text>
              <Text size="sm" c="dimmed">
                {reservation.customer_name || reservation.customer_phone || "Misafir"}
                {" · "}{reservation.party_size} kişi
                {reservation.notes ? ` · ${reservation.notes}` : ""}
              </Text>
            </Box>
          </Group>
        </Card>
      )}

      {/* ===== QR HESAP İSTEĞİ — müşterinin ödeme tercihi ===== */}
      {paymentRequest && (
        <Card padding="md" radius="md" withBorder bg="grape.0" style={{ borderColor: "var(--mantine-color-grape-4)" }}>
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Group gap={8} wrap="nowrap">
              <IconCash size={20} color="var(--mantine-color-grape-7)" />
              <Box>
                <Text fw={700} c="grape.7">
                  Müşteri hesap istedi
                </Text>
                <Text size="sm" c="dimmed">
                  {paymentRequest.split_mode === "split"
                    ? `Parçalı (bölüşmeli) ödeme — ${paymentRequest.split_parts} kişi`
                    : "Tek ödeme"}
                </Text>
              </Box>
            </Group>
            <Box ta="right">
              {paymentRequest.split_mode === "split" ? (
                <>
                  <Text fw={800} size="lg" c="grape.7">
                    {paymentRequest.split_parts} × {formatTRY(paymentRequest.per_part_minor)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    kişi başı
                  </Text>
                </>
              ) : (
                <Text fw={800} size="lg" c="grape.7">
                  {formatTRY(paymentRequest.total_minor)}
                </Text>
              )}
            </Box>
          </Group>
        </Card>
      )}

      {/* ===== TEMİZLİK: Masa boşaltma butonu — ödeme sonrası masa CLEANING'e
              alınır; garson temizlik bitince burada FREE'ye geçirir. Aksi halde
              masa "Temizlik" durumunda sabit kalır (auto-free ayarı 0 ise). ===== */}
      {state === "CLEANING" && !isOccupied && (
        <Card padding="md" radius="md" withBorder bg="gray.0">
          <Group justify="space-between" wrap="nowrap" align="center">
            <Group gap={8} wrap="nowrap">
              <IconSparkles size={20} color="var(--mantine-color-gray-7)" />
              <Box>
                <Text fw={700}>Masa temizlikte</Text>
                <Text size="xs" c="dimmed">
                  Temizlik bittiğinde masayı boşa al — yeni misafir kabul edilir.
                </Text>
              </Box>
            </Group>
            <Button
              color="green"
              leftSection={<IconCheck size={18} />}
              onClick={markTableFree}
              loading={freeing}
            >
              Temizlik Bitti
            </Button>
          </Group>
        </Card>
      )}

      {/* ===== DOLU MASA: Açık Hesap (accordion) + Hesap Al ===== */}
      {isOccupied && (
        <Card padding={0} radius="md" withBorder>
          {/* Adisyon (TableSession) üst başlığı — GİB Adisyon Tebliği:
              "1 masa = 1 adisyon, sonraki siparişler aynı adisyona işlenir".
              Kısa ID (short_id) + açılış saati görünür; alttaki Açık Hesap
              accordion'ında N farklı sipariş turu kartı yine ayrı kalır. */}
          {detail?.session && (
            <Box px="md" pt="sm">
              <Group gap={6} wrap="nowrap">
                <Badge size="md" variant="filled" color="indigo">
                  Adisyon #{detail.session.short_id}
                </Badge>
                <Text size="xs" c="dimmed">
                  · açıldı {formatOrderTime(detail.session.opened_at)}
                </Text>
                {detail.session.status === "LOCKED" && (
                  <Badge size="xs" variant="light" color="yellow">
                    Hesap istendi
                  </Badge>
                )}
              </Group>
            </Box>
          )}

          {/* Accordion başlığı — kapalıyken sadece bu görünür */}
          <UnstyledButton
            onClick={() => setAccountOpen((o) => !o)}
            style={{ width: "100%" }}
          >
            <Group justify="space-between" wrap="nowrap" p="md">
              <Group gap={8} wrap="nowrap">
                <IconReceipt2 size={20} />
                <Text fw={700}>
                  Açık Hesap ({openOrders.length} sipariş)
                </Text>
              </Group>
              <Group gap={6} wrap="nowrap">
                <Text fw={800} size="lg" c="blue">
                  {formatTRY(openTotal)}
                </Text>
                <IconChevronDown
                  size={18}
                  style={{
                    transition: "transform 150ms",
                    transform: accountOpen ? "rotate(180deg)" : "none",
                  }}
                />
              </Group>
            </Group>
          </UnstyledButton>

          {/* Açılınca: sipariş satırları */}
          <Collapse in={accountOpen}>
            <Stack gap="sm" px="md" pb="md">
              {openOrders.map((o, idx) => {
                const ks = KITCHEN_STATUS_BADGE[o.kitchen_status as string];
                return (
                <Card key={o.id} padding="sm" radius="sm" withBorder bg="gray.0">
                  <Group justify="space-between" mb={4} wrap="nowrap" align="flex-start">
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Group gap={6} wrap="wrap">
                        <Text size="sm" fw={600}>
                          #{idx + 1} ·{" "}
                          {o.source === "QR" ? "QR Müşteri" : o.waiter_name || "POS"}
                        </Text>
                        {ks && (
                          <Badge size="sm" color={ks.color} variant="light">
                            {ks.label}
                          </Badge>
                        )}
                        {/* Aktarım izi — başka masadan gelen sipariş için
                            vurgulu rozet (filled). Çok adımlı transferlerde
                            tüm zincir gösterilir: M3 → M4 → M5. Tek adımda
                            sadece kaynak masa adı.
                            transfer_chain yoksa eski alana düşer (geriye
                            uyumluluk: eski siparişler için). */}
                        {(o.transfer_chain && o.transfer_chain.length > 0)
                          ? (
                            <Badge size="sm" color="blue" variant="filled" leftSection="↪">
                              {o.transfer_chain.join(" → ")}'tan geldi
                            </Badge>
                          )
                          : o.transferred_from_table_name && (
                            <Badge size="sm" color="blue" variant="filled" leftSection="↪">
                              {o.transferred_from_table_name}'tan geldi
                            </Badge>
                          )}
                      </Group>
                      {/* Sipariş ID + zamanı — admin tablosuyla eşleşme için
                          kısa UUID prefix (8 char) saatin solunda. Aynı
                          garsonun paralel hesaplarını + admin tablosundaki
                          sipariş listesindeki ID'yi tek bakışta eşleştirmek
                          için. Confirmed zamanı varsa onu, yoksa created. */}
                      <Group gap={6} wrap="nowrap">
                        <Text size="xs" c="dimmed" ff="monospace">
                          {String(o.id).slice(0, 8)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          · 🕐 {formatOrderTime(o.confirmed_at || o.created_at)}
                        </Text>
                      </Group>
                    </Stack>
                    <Text size="sm" fw={700}>
                      {formatTRY(o.total_minor)}
                    </Text>
                  </Group>
                  <Stack gap={2}>
                    {(o.lines || []).map((ln: any) => (
                      <Group key={ln.id} justify="space-between" wrap="nowrap">
                        <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" lineClamp={1}>
                            {ln.quantity}× {ln.product_name}
                          </Text>
                          {ln.comp_quantity_approved > 0 && (
                            <Badge size="xs" color="pink" variant="light" style={{ flexShrink: 0 }}>
                              🎁 {ln.comp_quantity_approved} ikram
                            </Badge>
                          )}
                          {ln.returned_quantity_approved > 0 && (
                            <Badge size="xs" color="indigo" variant="light" style={{ flexShrink: 0 }}>
                              ↩ {ln.returned_quantity_approved} iade
                            </Badge>
                          )}
                          {ln.created_at && (
                            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                              · 🕐 {formatOrderTime(ln.created_at)}
                            </Text>
                          )}
                        </Group>
                        <Group gap={4} wrap="nowrap">
                          <Text size="sm" c="dimmed">
                            {formatTRY(ln.line_total_minor)}
                          </Text>
                          {/* İkram talebi — yemek hazırlanıp gittiyse (CONFIRMED+)
                              "müşteri beğenmedi" comp talebi. Müdür admin'den onaylar.
                              RBAC: talep açma yetkisi (COMP.CREATE) yoksa gizli. */}
                          {["CONFIRMED", "PREPARING", "READY", "SERVED"].includes(
                            o.kitchen_status as string,
                          ) && (
                            <RoleGate permission={{ resource: "COMP", action: "CREATE" }}>
                              <Tooltip label="İkram talebi (müşteri beğenmedi)">
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="pink"
                                  onClick={() => {
                                    setCompQty(1);
                                    setCompReason("CUSTOMER_DISSATISFACTION");
                                    setCompRemake(false);
                                    setCompTarget({
                                      lineId: ln.id,
                                      label: `${ln.quantity}× ${ln.product_name}`,
                                      maxQty: ln.quantity,
                                    });
                                  }}
                                >
                                  <IconGift size={15} />
                                </ActionIcon>
                              </Tooltip>
                            </RoleGate>
                          )}
                          {/* Ürün değişimi — "kola yerine ayran". Eski iade +
                              yeni eklenir; iade müdür onaylı.
                              RBAC: talep açma yetkisi (VOID.CREATE) yoksa gizli. */}
                          {["CONFIRMED", "PREPARING", "READY", "SERVED"].includes(
                            o.kitchen_status as string,
                          ) && (
                            <RoleGate permission={{ resource: "VOID", action: "CREATE" }}>
                              <Tooltip label="Ürünü değiştir (kola yerine ayran)">
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="indigo"
                                  onClick={() => {
                                    setExNewProduct(null);
                                    setExReturnQty(1);
                                    setExOpened(false);
                                    setExReason("CHANGED_MIND");
                                    setExchangeTarget({
                                      orderId: o.id,
                                      lineId: ln.id,
                                      label: `${ln.quantity}× ${ln.product_name}`,
                                      maxQty: ln.quantity,
                                    });
                                  }}
                                >
                                  <IconArrowsExchange size={15} />
                                </ActionIcon>
                              </Tooltip>
                            </RoleGate>
                          )}
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                  {/* Hesap Aktar artık adisyon seviyesinde tek buton (aşağıda,
                      "Hesap Al" yanında) — per-order kaldırıldı; tüm
                      siparişlerin kalemleri tek modalda seçilir.
                      İptal Et — yalnız DRAFT/OPEN/CONFIRMED. Mutfak
                      PREPARING'e geçince (aşçı "başla" basınca) garson
                      iptal edemez; ürün üretimi başlamış, malzeme tüketildi. */}
                  {["DRAFT", "OPEN", "CONFIRMED"].includes(
                    o.kitchen_status as string,
                  ) && (
                    <Group justify="center" mt={6} gap={6}>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<IconX size={14} />}
                        onClick={() => {
                          setCancelReason("");
                          setCancelTarget({
                            id: o.id,
                            label: `#${idx + 1} · ${o.waiter_name || "POS"}`,
                          });
                        }}
                      >
                        İptal Et
                      </Button>
                    </Group>
                  )}
                </Card>
                );
              })}
            </Stack>
          </Collapse>

          {/* Hesap Al — accordion açık/kapalı her durumda görünür.
              openTotal=0 ise (boş/silinmiş satırlı sipariş) yine tıklanabilir;
              handler sipariş(ler)i iptal edip masayı boşaltır. */}
          <Box px="md" pb="md">
            <Stack gap="xs">
              {/* Hesap Aktar — ADİSYON seviyesinde tek buton. Tüm açık
                  siparişlerin kalemlerinden seçim → hedef masada tek hesap. */}
              {openOrders.some(
                (o) => !["CLOSED", "CANCELLED"].includes(o.kitchen_status),
              ) && (
                <Button
                  fullWidth
                  variant="light"
                  color="blue"
                  leftSection={<IconArrowsExchange size={18} />}
                  onClick={() => setTransferOpen(true)}
                >
                  Hesap Aktar (Transfer)
                </Button>
              )}
              <Button
                fullWidth
                size="lg"
                leftSection={<IconCash size={20} />}
                onClick={handleHesapAl}
                loading={closing}
              >
                {openTotal > 0
                  ? `Hesap Al · ${formatTRY(openTotal)}`
                  : "Masayı Kapat"}
              </Button>
            </Stack>
          </Box>
        </Card>
      )}

      {/* ===== YENİ ÜRÜN EKLE — başlık + (+) ile katlanır ===== */}
      <Card padding={0} radius="md" withBorder>
        <UnstyledButton
          onClick={() => setMenuOpen((o) => !o)}
          style={{ width: "100%" }}
        >
          <Group justify="space-between" wrap="nowrap" p="md">
            <Box>
              <Group gap={8} wrap="nowrap">
                {/* Masa adı sticker'ı — hangi masaya sipariş alındığı tek
                    bakışta belli olsun (başlık satırı header'a taşındığından
                    masa adı buraya rozet olarak gelir). */}
                <Badge
                  size="lg"
                  variant="filled"
                  color={STATE_COLOR[state] || "blue"}
                  radius="sm"
                  style={{ flexShrink: 0 }}
                >
                  {table?.name || "Masa"}
                </Badge>
                <Text fw={700} size="lg">
                  {isOccupied ? "Yeni Ürün Ekle" : "Sipariş Al"}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {cartCount > 0
                  ? `${cart.length} kalem, ${cartCount} adet sepette`
                  : table?.capacity
                    ? `${table.capacity} kişi · eklemek için dokunun`
                    : "Eklemek için dokunun"}
              </Text>
            </Box>
            {/* Görsel ikon — tıklama dış UnstyledButton'a ait. <button>
                iç içe geçmesin diye ActionIcon değil Box kullanılır. */}
            <Box
              aria-hidden
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                background: menuOpen
                  ? "var(--mantine-color-blue-1)"
                  : "var(--mantine-color-blue-6)",
                color: menuOpen
                  ? "var(--mantine-color-blue-7)"
                  : "white",
              }}
            >
              {menuOpen ? <IconMinus size={22} /> : <IconPlus size={22} />}
            </Box>
          </Group>
        </UnstyledButton>

        <Collapse in={menuOpen}>
          <Box px="md" pb="md">
            {cats.length === 0 ? (
              <EmptyState
                title="Henüz kategori yok"
                description="Yöneticinizden menü tanımlamasını isteyin."
              />
            ) : (
              <Stack gap="sm">
                {/* Üst şerit — kök kategoriler (QR menüdeki üst sekme) */}
                <ScrollArea type="never" offsetScrollbars>
                  <Group gap="xs" wrap="nowrap" pb={4}>
                    {rootCats.map((c: any) => (
                      <Button
                        key={c.id}
                        variant={activeRoot === c.id ? "filled" : "default"}
                        onClick={() => setActiveRoot(c.id)}
                        size="md"
                        style={{ flexShrink: 0 }}
                      >
                        {c.name || "Kategori"}
                      </Button>
                    ))}
                  </Group>
                </ScrollArea>

                {/* Alt şerit — seçili kökün alt kategorileri (QR menüde
                    alt sekme). Kök doğrudan ürün taşıyorsa hiç çıkmaz. */}
                {childCats.length > 0 && (
                  <ScrollArea type="never" offsetScrollbars>
                    <Group gap="xs" wrap="nowrap" pb={4}>
                      {childCats.map((c: any) => (
                        <Button
                          key={c.id}
                          variant={activeChild === c.id ? "light" : "subtle"}
                          color="blue"
                          onClick={() => setActiveChild(c.id)}
                          size="sm"
                          style={{ flexShrink: 0 }}
                        >
                          {c.name || "Kategori"}
                        </Button>
                      ))}
                      {rootOwnCount > 0 && (
                        <Button
                          key={activeRoot}
                          variant={activeChild === activeRoot ? "light" : "subtle"}
                          color="blue"
                          onClick={() => setActiveChild(activeRoot)}
                          size="sm"
                          style={{ flexShrink: 0 }}
                        >
                          {activeRootNode?.name || "Diğer"}
                        </Button>
                      )}
                    </Group>
                  </ScrollArea>
                )}

                {prodsLoading ? (
                  <Center h={160}>
                    <Loader />
                  </Center>
                ) : products.length === 0 ? (
                  <Text c="dimmed" ta="center" py="xl">
                    Bu kategoride ürün yok
                  </Text>
                ) : (
                  <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
                    {products.map((p: any) => (
                      <Card
                        key={p.id}
                        padding="md"
                        radius="md"
                        withBorder
                        onClick={() => addItem(p)}
                        style={{ cursor: "pointer", minHeight: 90 }}
                      >
                        <Stack gap={4}>
                          <Text fw={600} lineClamp={2}>
                            {p.name}
                          </Text>
                          <Text fw={700} c="blue">
                            {formatTRY(
                              p.unit_price_minor || p.price_minor || 0,
                            )}
                          </Text>
                        </Stack>
                      </Card>
                    ))}
                  </SimpleGrid>
                )}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Card>

      {/* Modifier seçim modalı */}
      <Modal
        opened={!!modifierProduct}
        onClose={() => setModifierProduct(null)}
        title={modifierProduct ? `${modifierProduct.name} — Özelleştir` : ""}
        size="md"
      >
        {modifierProduct && (
          <Stack gap="md">
            <Card padding="sm" radius="md" withBorder bg="gray.0">
              <Group justify="space-between">
                <Text>{modifierProduct.name}</Text>
                <Text fw={700}>
                  {formatTRY(
                    modifierProduct.unit_price_minor ||
                      modifierProduct.price_minor ||
                      0,
                  )}
                </Text>
              </Group>
            </Card>

            {productModifiers.length > 0 ? (
              <Stack gap="xs">
                <Text fw={600} size="sm">
                  Modifier'lar (opsiyonel)
                </Text>
                {productModifiers.map((m) => (
                  <Checkbox
                    key={m.id}
                    label={
                      <Group justify="space-between" style={{ width: "100%" }}>
                        <Text>{m.name}</Text>
                        {m.price_minor > 0 && (
                          <Text c="dimmed">{formatTRY(m.price_minor)}</Text>
                        )}
                      </Group>
                    }
                    checked={selectedMods.includes(m.id)}
                    onChange={(e) => {
                      if (e.currentTarget.checked)
                        setSelectedMods([...selectedMods, m.id]);
                      else
                        setSelectedMods(
                          selectedMods.filter((x) => x !== m.id),
                        );
                    }}
                  />
                ))}
              </Stack>
            ) : (
              <Text size="xs" c="dimmed">
                Bu ürün için modifier tanımlı değil.
              </Text>
            )}

            <Textarea
              label="Not (opsiyonel)"
              placeholder="Az pişmiş, soğansız vs..."
              value={lineNotes}
              onChange={(e) => setLineNotes(e.currentTarget.value)}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModifierProduct(null)}>
                İptal
              </Button>
              <Button onClick={confirmAdd}>Sepete Ekle</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ===== SEPET — en altta sabit "Sepet Detayı" şeridi ===== */}
      {cart.length > 0 && (
        <Box
          style={{
            position: "fixed",
            bottom: "calc(70px + env(safe-area-inset-bottom, 0px))",
            left: 0,
            right: 0,
            zIndex: 90,
          }}
        >
          {/* Açılır sepet paneli — şeridin üstünde belirir */}
          <Collapse in={cartOpen}>
            <Card
              padding="md"
              radius={0}
              withBorder
              style={{
                maxHeight: "50vh",
                overflowY: "auto",
                boxShadow: "0 -4px 12px rgba(0,0,0,0.12)",
              }}
            >
              <Stack gap="xs">
                <Group gap={6}>
                  <IconToolsKitchen2 size={18} />
                  <Text fw={700}>Sepet</Text>
                </Group>
                {cart.map((c) => (
                  <Group key={c.id} justify="space-between" wrap="nowrap">
                    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                      <Text lineClamp={1}>{c.name}</Text>
                      {c.modifiers && c.modifiers.length > 0 ? (
                        <Text size="xs" c="yellow.7" lineClamp={1}>
                          {c.modifiers
                            .map((m) =>
                              m.price_minor
                                ? `+ ${m.name} (${formatTRY(m.price_minor)})`
                                : `+ ${m.name}`,
                            )
                            .join(", ")}
                        </Text>
                      ) : c.modifier_names.length > 0 ? (
                        <Text size="xs" c="yellow.7" lineClamp={1}>
                          + {c.modifier_names.join(", ")}
                        </Text>
                      ) : null}
                      {c.excluded_ingredient_names && c.excluded_ingredient_names.length > 0 && (
                        <Text size="xs" c="red.7" lineClamp={1}>
                          🚫 {c.excluded_ingredient_names.join(", ")} hariç
                        </Text>
                      )}
                      {c.notes && (
                        <Text size="xs" c="dimmed" fs="italic" lineClamp={1}>
                          {c.notes}
                        </Text>
                      )}
                    </Stack>
                    <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <ActionIcon
                        size="md"
                        variant="default"
                        onClick={() => changeQty(c.id, -1)}
                      >
                        <IconMinus size={14} />
                      </ActionIcon>
                      <Badge size="lg" variant="filled">
                        {c.qty}
                      </Badge>
                      <ActionIcon
                        size="md"
                        variant="default"
                        onClick={() => changeQty(c.id, +1)}
                      >
                        <IconPlus size={14} />
                      </ActionIcon>
                      <Text fw={700} w={78} ta="right">
                        {formatTRY(c.price * c.qty)}
                      </Text>
                    </Group>
                  </Group>
                ))}
                <Divider />
                <Group grow>
                  <Button variant="default" onClick={() => setCart([])}>
                    <IconTrash size={18} /> Temizle
                  </Button>
                  <Button
                    onClick={handleSendToKitchen}
                    loading={
                      createOrder.isPending ||
                      addLine.isPending ||
                      confirmOrder.isPending
                    }
                  >
                    Onayla ve Gönder
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Collapse>

          {/* Sabit "Sepet Detayı" şeridi — her zaman görünür, tıklanınca açılır */}
          <UnstyledButton
            onClick={() => setCartOpen((o) => !o)}
            style={{
              display: "block",
              width: "100%",
              background: "var(--mantine-color-blue-6)",
              color: "white",
              padding: "12px 16px",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.15)",
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap={8} wrap="nowrap">
                <IconShoppingCart size={20} />
                <Text fw={700}>Sepet Detayı ({cartCount} ürün)</Text>
                <IconChevronDown
                  size={16}
                  style={{
                    transition: "transform 150ms",
                    transform: cartOpen ? "rotate(180deg)" : "none",
                  }}
                />
              </Group>
              <Text fw={800} size="lg">
                {formatTRY(cartTotal)}
              </Text>
            </Group>
          </UnstyledButton>
        </Box>
      )}

      {/* ===== HESAP AL — tek/parçalı ödeme =====
          Yalnız tutarı olan siparişler ödemeye girer. Boş/onay bekleyen
          (total_minor=0) siparişlere tender atmak "Order already fully
          paid" hatası verir — onları hesaba dahil etme. */}
      <PaymentSheet
        opened={payOpen}
        onClose={() => setPayOpen(false)}
        orders={openOrders.filter((o) => (o.total_minor || 0) > 0)}
        sessionId={detail?.session?.id || null}
        onPaid={() => {
          setPayOpen(false);
          notifications.show({
            message: "Ödeme alındı, masa kapatıldı",
            color: "green",
          });
          onBack();
        }}
      />

      {/* ===== HESAP AKTAR — satır seçimli masalar arası transfer =====
          Garson "şu kişinin payı M5'e geçti" der; seçili satırlar hedef
          masaya taşınır, kalan satırlar burada açık kalır. */}
      <TransferLinesModal
        opened={transferOpen}
        onClose={() => setTransferOpen(false)}
        sourceTableId={tableId}
        orders={openOrders.filter(
          (o) => !["CLOSED", "CANCELLED"].includes(o.kitchen_status),
        )}
        onTransferred={() => {
          // Modal kendi kapanmasını sağlar; burada masa cache zaten
          // useTransferSessionLines onSuccess'inde invalidate ediliyor.
        }}
      />

      {/* ===== Hesap alırken bekleyen QR siparişi uyarısı ===== */}
      <Modal
        opened={pendingPrompt}
        onClose={() => {
          if (approveReq.isPending || rejectReq.isPending) return;
          setPendingPrompt(false);
        }}
        title="Onay bekleyen sipariş var"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Bu masada müşterinin <strong>onay bekleyen {pendingForTable.length} siparişi</strong> var.
            Hesabı kapatırsanız bu sipariş(ler) iptal edilir. Ne yapmak istersiniz?
          </Text>

          {/* Bekleyen istek özetleri */}
          <Stack gap={4}>
            {pendingForTable.map((r: any) => (
              <Group key={r.id} justify="space-between" wrap="nowrap">
                <Text size="sm" lineClamp={1}>
                  {(r.lines || [])
                    .map((l: any) => `${l.quantity}× ${l.product_name}`)
                    .join(", ") || "Sipariş"}
                </Text>
                <Text size="sm" fw={600}>{formatTRY(r.total_minor || 0)}</Text>
              </Group>
            ))}
          </Stack>

          <Stack gap="xs">
            <Button
              color="green"
              leftSection={<IconCheck size={18} />}
              loading={approveReq.isPending}
              onClick={approvePendingThenCheckout}
            >
              Siparişi Onayla (mutfağa gönder)
            </Button>
            <Button
              color="red"
              variant="light"
              leftSection={<IconX size={18} />}
              loading={rejectReq.isPending}
              onClick={rejectPendingThenCheckout}
            >
              İptal Et ve Hesap Al
            </Button>
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setPendingPrompt(false)}
              disabled={approveReq.isPending || rejectReq.isPending}
            >
              Vazgeç
            </Button>
          </Stack>
        </Stack>
      </Modal>

      {/* ===== Sipariş iptal modalı ===== */}
      <Modal
        opened={!!cancelTarget}
        onClose={() => {
          if (cancelOrder.isPending) return;
          setCancelTarget(null);
          setCancelReason("");
        }}
        title="Siparişi İptal Et"
        centered
        zIndex={300}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {cancelTarget?.label} iptal edilecek. İsterseniz sebep
            yazabilirsiniz (opsiyonel, raporlarda görünür).
          </Text>
          <Textarea
            label="İptal sebebi"
            placeholder="Örn: müşteri vazgeçti, yanlış sipariş..."
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={5}
            data-autofocus
          />
          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              onClick={() => {
                setCancelTarget(null);
                setCancelReason("");
              }}
              disabled={cancelOrder.isPending}
            >
              Vazgeç
            </Button>
            <Button
              color="red"
              leftSection={<IconX size={14} />}
              loading={cancelOrder.isPending}
              onClick={() => {
                if (!cancelTarget) return;
                cancelOrder.mutate(
                  {
                    orderId: cancelTarget.id,
                    reason: cancelReason.trim() || "Garson iptali",
                  },
                  {
                    onSuccess: () => {
                      notifications.show({
                        message: "Sipariş iptal edildi",
                        color: "green",
                      });
                      qc.invalidateQueries({ queryKey: ["table-detail"] });
                      qc.invalidateQueries({ queryKey: ["tables"] });
                      setCancelTarget(null);
                      setCancelReason("");
                    },
                    onError: (e: any) => {
                      notifications.show({
                        message:
                          e?.response?.data?.detail ||
                          "Sipariş iptal edilemedi",
                        color: "red",
                      });
                    },
                  },
                );
              }}
            >
              Siparişi İptal Et
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ===== İkram (comp) talep modalı ===== */}
      <Modal
        opened={!!compTarget}
        onClose={() => {
          if (requestComp.isPending) return;
          setCompTarget(null);
        }}
        title="İkram Talebi (Müşteri Beğenmedi)"
        centered
        zIndex={300}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            <b>{compTarget?.label}</b> için ikram talebi açılacak. Müdür
            onayından sonra bedelden düşülür (stok COMPLIMENTARY). Reddedilirse
            etkisiz olur.
          </Text>
          <NumberInput
            label="Kaç porsiyon ikram"
            value={compQty}
            onChange={setCompQty}
            min={1}
            max={compTarget?.maxQty ?? 1}
            allowDecimal={false}
            description={`Bu satırda ${compTarget?.maxQty ?? 1} porsiyon var`}
          />
          <Select
            label="Sebep"
            value={compReason}
            onChange={(v) => v && setCompReason(v)}
            data={[
              { value: "CUSTOMER_DISSATISFACTION", label: "Müşteri Beğenmedi" },
              { value: "KITCHEN_ERROR", label: "Mutfak Hatası" },
              { value: "LONG_WAIT", label: "Uzun Bekleme" },
              { value: "GOODWILL", label: "Jest / İyi Niyet" },
              { value: "OTHER", label: "Diğer" },
            ]}
          />
          <Checkbox
            label="Yeniden hazırlansın (remake — mutfağa yeni tabak)"
            checked={compRemake}
            onChange={(e) => setCompRemake(e.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              onClick={() => setCompTarget(null)}
              disabled={requestComp.isPending}
            >
              Vazgeç
            </Button>
            <Button
              color="pink"
              leftSection={<IconGift size={14} />}
              loading={requestComp.isPending}
              onClick={() => {
                if (!compTarget) return;
                requestComp.mutate(
                  {
                    order_line_id: compTarget.lineId,
                    comp_quantity: Number(compQty),
                    reason_code: compReason,
                    remake: compRemake,
                  },
                  {
                    onSuccess: () => {
                      notifications.show({
                        title: "İkram talebi gönderildi",
                        message: "Müdür onayı bekleniyor",
                        color: "green",
                      });
                      setCompTarget(null);
                    },
                    onError: (e: any) =>
                      notifications.show({
                        message: e?.response?.data?.detail || "Hata",
                        color: "red",
                      }),
                  },
                );
              }}
            >
              Talep Gönder
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ===== Ürün değişimi (exchange) modalı ===== */}
      <Modal
        opened={!!exchangeTarget}
        onClose={() => { if (!exchangeLine.isPending) setExchangeTarget(null); }}
        title="Ürün Değiştir"
        centered
        zIndex={300}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            <b>{exchangeTarget?.label}</b> iade edilip yeni ürün eklenecek.
            Yeni ürün hemen mutfağa gider; iade <b>müdür onayı</b> bekler
            (açılmamışsa onayda stoğa geri döner, açılmışsa kayıp sayılır).
          </Text>
          <Select
            label="Yeni ürün"
            placeholder="Değiştirilecek ürünü seç"
            searchable
            data={allProducts.map((p) => ({
              value: p.id,
              label: `${p.name} — ${formatTRY(p.unit_price_minor)}`,
            }))}
            value={exNewProduct}
            onChange={setExNewProduct}
          />
          <NumberInput
            label="İade porsiyonu"
            value={exReturnQty}
            onChange={setExReturnQty}
            min={1}
            max={exchangeTarget?.maxQty ?? 1}
            allowDecimal={false}
            description={`Bu satırda ${exchangeTarget?.maxQty ?? 1} porsiyon var`}
          />
          <Checkbox
            label="Ürün açıldı / tüketildi (iade alınamaz → kayıp, müdür onaylı)"
            checked={exOpened}
            onChange={(e) => setExOpened(e.currentTarget.checked)}
          />
          <Select
            label="Sebep"
            value={exReason}
            onChange={(v) => v && setExReason(v)}
            data={[
              { value: "CHANGED_MIND", label: "Fikir Değiştirdi (başka ürün)" },
              { value: "WRONG_ITEM", label: "Yanlış Ürün Geldi" },
              { value: "QUALITY", label: "Kalite / Beğenmedi" },
              { value: "OTHER", label: "Diğer" },
            ]}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setExchangeTarget(null)}
              disabled={exchangeLine.isPending}>
              Vazgeç
            </Button>
            <Button
              color="indigo"
              leftSection={<IconArrowsExchange size={14} />}
              loading={exchangeLine.isPending}
              disabled={!exNewProduct}
              onClick={() => {
                if (!exchangeTarget || !exNewProduct) return;
                exchangeLine.mutate(
                  {
                    orderId: exchangeTarget.orderId,
                    order_line_id: exchangeTarget.lineId,
                    new_product_id: exNewProduct,
                    new_quantity: 1,
                    opened: exOpened,
                    reason_code: exReason,
                    return_quantity: Number(exReturnQty),
                  },
                  {
                    onSuccess: () => {
                      notifications.show({
                        title: "Değişim yapıldı",
                        message: "Yeni ürün eklendi — iade müdür onayı bekliyor",
                        color: "green",
                      });
                      setExchangeTarget(null);
                    },
                    onError: (e: any) =>
                      notifications.show({
                        message: e?.response?.data?.detail || "Hata",
                        color: "red",
                      }),
                  },
                );
              }}
            >
              Değiştir
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ===== Stok yetersizliği pop-up'ı =====
          Backend add_line 400 + shortages payload → garson dostu liste.
          is_optional malzemeler için "hepsini çıkar ve gönder" butonu;
          zorunlu malzeme varsa o seçenek devre dışı, garson stok girip
          tekrar denemeli. */}
      {shortageDialog && (
        <ShortageDialog
          state={shortageDialog}
          onClose={() => {
            // Vazgeçildi — bu turda biz yarattıysak ve hiç satır eklenemedi
            // ise arkada yetim DRAFT bırakma. Cancel fire-and-forget; UI
            // bloklamaz.
            if (shortageDialog.orphanOrderId) {
              cancelOrder.mutate({
                orderId: shortageDialog.orphanOrderId,
                reason: "Stok yetersiz — siparişe satır eklenemeden iptal",
              });
            }
            setShortageDialog(null);
          }}
          onExcludeAll={() => {
            // Sepetteki ilgili satıra exclusion'ları işle; pop-up'ı kapat;
            // garson "Onayla ve Gönder"'e tekrar bassın. Mevcut yetim
            // DRAFT'ı da iptal et — kullanıcı bir sonraki turda mevcut
            // siparişlerde onu DRAFT olarak görüp APPENDABLE listesinde
            // tekrar bulup eklemeye çalışmasın (boş ama DRAFT bir order).
            if (shortageDialog.orphanOrderId) {
              cancelOrder.mutate({
                orderId: shortageDialog.orphanOrderId,
                reason: "Stok yetersiz — yeniden denenecek",
              });
            }
            setCart((prev) => prev.map((c) =>
              c.id === shortageDialog.cartItemId
                ? {
                    ...c,
                    excluded_ingredient_ids: shortageDialog.shortages
                      .filter((s) => s.is_optional)
                      .map((s) => s.ingredient_id),
                    excluded_ingredient_names: shortageDialog.shortages
                      .filter((s) => s.is_optional)
                      .map((s) => s.name),
                  }
                : c,
            ));
            setShortageDialog(null);
            notifications.show({
              color: "blue",
              message: "Eksik malzemeler çıkarıldı, tekrar 'Onayla ve Gönder'e basın.",
            });
          }}
        />
      )}
    </Stack>
  );
}

